import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Avatar, Input, Spin, Table, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import dayjs from 'dayjs'

import {
    fetchWorkloadProjectTimelog,
    fetchWorkloadUserTimelogList,
    closeWorkloadTimelogDrawer,
    openWorkloadTimelogDrawer,
    workloadState,
    setWorkloadFilters,
} from './workload.slice'
import {
    fetchProjectsForClientModal,
    clientsState,
} from '../../clients/clients.slice'
import {
    formatLogDuration,
    getDefaultReportingDateRange,
    getInitials,
    getReportingDateRangeForPeriod,
    REPORTING_PERIOD,
    toReportingDateTimestamp,
} from './workload.utils'
import WorkloadLogProgress from './components/WorkloadLogProgress'
import WorkloadPeriodControls from './components/WorkloadPeriodControls'
import WorkloadTimelogDetailPanel from './components/WorkloadTimelogDetailPanel'
import ProjectMultiSelect from '../../../components/ProjectMultiSelect'
import { PRIVATE_ROUTES } from '../../../routes'

import styles from './WorkloadDashboard.module.css'

const { Title } = Typography

const WORKLOAD_SCROLL_TARGET_ID = 'workload-scroll-container'

const WorkloadDashboard = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {
        status,
        error,
        rows,
        search,
        totalCount,
        totalLog,
        isNext,
        projectIds,
        periodType,
        start,
        end,
        detail,
    } = useSelector(workloadState)
    const selectedMemberId = detail?.open ? detail?.user?._id : null
    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)
    const loadingMoreRef = useRef(false)

    const isLoading = status === 'loading'
    const isLoadingMore = status === 'loadingMore'

    useEffect(() => {
        loadingMoreRef.current = isLoadingMore
    }, [isLoadingMore])

    const loadMoreRows = useCallback(() => {
        if (!isNext || loadingMoreRef.current || isLoading) return
        dispatch(fetchWorkloadUserTimelogList({ reset: false }))
    }, [dispatch, isNext, isLoading])

    const applyFiltersAndFetch = useCallback(
        (patch = {}) => {
            dispatch(
                setWorkloadFilters({
                    search: patch.search ?? search,
                    start: patch.start ?? start,
                    end: patch.end ?? end,
                    periodType: patch.periodType ?? periodType,
                    projectIds: patch.projectIds ?? projectIds,
                })
            )
            dispatch(fetchWorkloadUserTimelogList({ reset: true }))
            if (detail?.open && detail?.user?._id) {
                dispatch(fetchWorkloadProjectTimelog())
            }
        },
        [
            dispatch,
            search,
            start,
            end,
            periodType,
            projectIds,
            detail?.open,
            detail?.user?._id,
        ]
    )

    const handlePeriodChange = useCallback(
        (nextPeriod) => {
            if (nextPeriod === periodType) return

            let range
            if (nextPeriod === REPORTING_PERIOD.CUSTOM) {
                if (start != null && end != null) {
                    range = {
                        start: toReportingDateTimestamp(start, 'start'),
                        end: toReportingDateTimestamp(end, 'end'),
                    }
                } else {
                    range = getReportingDateRangeForPeriod(
                        REPORTING_PERIOD.CUSTOM
                    )
                }
            } else {
                const ref = start != null ? dayjs(start) : dayjs()
                range = getReportingDateRangeForPeriod(nextPeriod, ref)
            }

            applyFiltersAndFetch({
                periodType: nextPeriod,
                ...range,
            })
        },
        [applyFiltersAndFetch, periodType, start, end]
    )

    const handlePresetRangeChange = useCallback(
        (range) => {
            applyFiltersAndFetch(range)
        },
        [applyFiltersAndFetch]
    )

    const handleCustomRangeChange = useCallback(
        (range) => {
            if (!range?.start || !range?.end) {
                applyFiltersAndFetch(
                    getReportingDateRangeForPeriod(REPORTING_PERIOD.CUSTOM)
                )
                return
            }
            applyFiltersAndFetch({
                start: toReportingDateTimestamp(range.start, 'start'),
                end: toReportingDateTimestamp(range.end, 'end'),
            })
        },
        [applyFiltersAndFetch]
    )

    const handleViewTimelogDetail = useCallback(
        (record) => {
            if (!record?._id) return
            dispatch(openWorkloadTimelogDrawer(record))
            dispatch(fetchWorkloadProjectTimelog())
        },
        [dispatch]
    )

    const handleResetFilters = useCallback(() => {
        const defaults = getDefaultReportingDateRange()
        dispatch(closeWorkloadTimelogDrawer())
        dispatch(
            setWorkloadFilters({
                search: '',
                periodType: REPORTING_PERIOD.DAY,
                ...defaults,
                projectIds: [],
            })
        )
        dispatch(fetchWorkloadUserTimelogList({ reset: true }))
    }, [dispatch])

    useEffect(() => {
        const defaults = getDefaultReportingDateRange()
        dispatch(
            setWorkloadFilters({
                search: '',
                periodType: REPORTING_PERIOD.DAY,
                ...defaults,
                projectIds: [],
                userIds: [],
            })
        )
        dispatch(fetchWorkloadUserTimelogList({ reset: true }))
    }, [dispatch])

    const debouncedProjectSearch = useMemo(
        () =>
            debounce((value) => {
                dispatch(
                    fetchProjectsForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                    })
                )
            }, 300),
        [dispatch]
    )

    useEffect(
        () => () => debouncedProjectSearch.cancel(),
        [debouncedProjectSearch]
    )

    const handleProjectFilterOpen = useCallback(
        (open) => {
            if (!open) return
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )
        },
        [dispatch]
    )

    const handleProjectFilterScroll = useCallback(
        (e) => {
            const { target } = e
            if (
                target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50 &&
                clientModalIsNext &&
                clientModalListStatus !== 'loading'
            ) {
                const nextPage = clientModalProjectPage + 1
                const skip = (nextPage - 1) * 10
                dispatch(
                    fetchProjectsForClientModal({
                        skip,
                        filter: clientModalProjectSearch
                            ? { search: clientModalProjectSearch }
                            : {},
                        limit: 10,
                        search: clientModalProjectSearch,
                    })
                )
            }
        },
        [
            dispatch,
            clientModalIsNext,
            clientModalListStatus,
            clientModalProjectPage,
            clientModalProjectSearch,
        ]
    )

    const debouncedFetch = useMemo(
        () =>
            debounce(() => {
                dispatch(fetchWorkloadUserTimelogList({ reset: true }))
            }, 350),
        [dispatch]
    )

    useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch])

    const columns = useMemo(
        () => [
            {
                title: 'Team member',
                dataIndex: 'name',
                key: 'name',
                render: (_, record) => (
                    <div className={styles.userCell}>
                        <Avatar
                            size={36}
                            src={record?.profilePicture}
                            style={{
                                backgroundColor: '#6366f1',
                                flexShrink: 0,
                            }}
                        >
                            {!record?.profilePicture
                                ? getInitials(record?.name)
                                : null}
                        </Avatar>
                        <div className={styles.userMeta}>
                            <div className={styles.userName}>
                                {record?.name?.trim() || 'Unnamed'}
                            </div>
                            <div className={styles.userEmail}>
                                {record?.email || '—'}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                title: 'Total logged',
                dataIndex: 'totalLog',
                key: 'totalLog',
                width: 240,
                render: (_, record) => <WorkloadLogProgress record={record} />,
            },
            {
                title: 'Projects',
                dataIndex: 'projects',
                key: 'projects',
                render: (projects) => {
                    if (!projects?.length) {
                        return (
                            <span className={styles.emptyProjects}>
                                No project logs in range
                            </span>
                        )
                    }
                    return (
                        <div className={styles.projectsPillsWrap}>
                            {projects.map((p) => (
                                <span
                                    key={p?.projectId}
                                    className={styles.projectPill}
                                    title={p?.projectName || 'Project'}
                                >
                                    <span
                                        className={styles.projectSwatch}
                                        style={{
                                            backgroundColor:
                                                p?.colorCode || '#94a3b8',
                                        }}
                                    />
                                    <span className={styles.projectPillName}>
                                        {p?.projectName || 'Project'}
                                    </span>
                                    <span className={styles.projectLog}>
                                        {formatLogDuration(p?.totalLog)}
                                    </span>
                                </span>
                            ))}
                        </div>
                    )
                },
            },
        ],
        []
    )

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button
                    type="button"
                    className={styles.backLink}
                    onClick={() => navigate(PRIVATE_ROUTES.reporting)}
                >
                    <Icon icon="mdi:arrow-left" width={18} height={18} />
                    All reports
                </button>

                <div className={styles.headerTop}>
                    <div className={styles.headerText}>
                        <Title level={3} className={styles.title}>
                            Workload
                        </Title>
                        <p className={styles.subtitle}>
                            User workload and time logged across projects
                        </p>
                    </div>

                    <div className={styles.headerControls}>
                        <div className={styles.searchWrap}>
                            <Input
                                allowClear
                                placeholder="Search by name or email"
                                className={styles.search}
                                prefix={
                                    <Icon
                                        icon="mdi:magnify"
                                        width={18}
                                        height={18}
                                    />
                                }
                                value={search}
                                onChange={(e) => {
                                    dispatch(
                                        setWorkloadFilters({
                                            search: e.target.value,
                                        })
                                    )
                                    debouncedFetch()
                                }}
                            />
                        </div>

                        <WorkloadPeriodControls
                            periodType={periodType}
                            start={start}
                            end={end}
                            onPeriodChange={handlePeriodChange}
                            onPresetRangeChange={handlePresetRangeChange}
                            onCustomRangeChange={handleCustomRangeChange}
                        />

                        <ProjectMultiSelect
                            wrapClassName={styles.projectFilterWrap}
                            projects={clientModalProjectsListData}
                            value={projectIds}
                            onChange={(ids) =>
                                applyFiltersAndFetch({ projectIds: ids })
                            }
                            placeholder="Select Projects"
                            showSearch
                            filterOption={false}
                            onSearch={debouncedProjectSearch}
                            onPopupScroll={handleProjectFilterScroll}
                            loading={clientModalListStatus === 'loading'}
                            page={clientModalProjectPage}
                            onOpenChange={handleProjectFilterOpen}
                        />

                        <button
                            type="button"
                            className={styles.pillBtn}
                            onClick={handleResetFilters}
                        >
                            Reset filters
                        </button>
                    </div>

                    <div className={styles.summaryRow}>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>Members</div>
                            <div className={styles.summaryValue}>
                                {totalCount}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>
                                Total logged
                            </div>
                            <div className={styles.summaryValue}>
                                {formatLogDuration(totalLog)}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className={styles.content}>
                {status === 'error' ? (
                    <div className={styles.errorState}>
                        <div className={styles.errorIconBadge} aria-hidden>
                            <Icon
                                icon="mdi:cloud-alert-outline"
                                width={26}
                                height={26}
                                className={styles.errorIcon}
                            />
                        </div>
                        <p className={styles.errorTitle}>
                            Unable to load reporting data
                        </p>
                        <p className={styles.errorMessage}>
                            {error || 'Something went wrong. Please try again.'}
                        </p>
                        <button
                            type="button"
                            className={styles.errorRetryBtn}
                            onClick={() =>
                                dispatch(
                                    fetchWorkloadUserTimelogList({
                                        reset: true,
                                    })
                                )
                            }
                        >
                            <Icon icon="mdi:refresh" width={16} />
                            Try again
                        </button>
                    </div>
                ) : (
                    <div className={styles.masterDetail}>
                        <div
                            id={WORKLOAD_SCROLL_TARGET_ID}
                            className={styles.masterPane}
                        >
                            <Spin spinning={isLoading && rows.length === 0}>
                                <InfiniteScroll
                                    dataLength={rows.length}
                                    next={loadMoreRows}
                                    hasMore={isNext}
                                    initialLoad={false}
                                    scrollThreshold="200px"
                                    scrollableTarget={WORKLOAD_SCROLL_TARGET_ID}
                                    loader={
                                        isLoadingMore ? (
                                            <div
                                                className={styles.scrollLoader}
                                            >
                                                <Icon
                                                    icon="line-md:loading-loop"
                                                    style={{
                                                        fontSize: 26,
                                                        color: '#6366f1',
                                                    }}
                                                />
                                            </div>
                                        ) : null
                                    }
                                >
                                    <div className={styles.tableWrap}>
                                        <Table
                                            rowKey="_id"
                                            columns={columns}
                                            dataSource={rows}
                                            pagination={false}
                                            scroll={{ x: 'max-content' }}
                                            rowClassName={(record) =>
                                                record?._id === selectedMemberId
                                                    ? styles.tableRowSelected
                                                    : ''
                                            }
                                            onRow={(record) => ({
                                                onClick: () =>
                                                    handleViewTimelogDetail(
                                                        record
                                                    ),
                                                style: { cursor: 'pointer' },
                                            })}
                                            locale={{
                                                emptyText: (
                                                    <div
                                                        className={
                                                            styles.tableEmptyState
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.tableEmptyIconBadge
                                                            }
                                                            aria-hidden
                                                        >
                                                            <Icon
                                                                icon="mdi:clock-time-four-outline"
                                                                width={28}
                                                                height={28}
                                                                className={
                                                                    styles.tableEmptyIcon
                                                                }
                                                            />
                                                        </div>
                                                        <p
                                                            className={
                                                                styles.tableEmptyTitle
                                                            }
                                                        >
                                                            No timelog data for
                                                            this period
                                                        </p>
                                                        <p
                                                            className={
                                                                styles.tableEmptyHint
                                                            }
                                                        >
                                                            No team members
                                                            logged time in the
                                                            selected range. Try
                                                            widening the date
                                                            range or clearing
                                                            project filters.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.tableEmptyAction
                                                            }
                                                            onClick={
                                                                handleResetFilters
                                                            }
                                                        >
                                                            <Icon
                                                                icon="mdi:filter-off-outline"
                                                                width={16}
                                                            />
                                                            Reset filters
                                                        </button>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </div>
                                </InfiniteScroll>
                            </Spin>
                        </div>

                        <WorkloadTimelogDetailPanel />
                    </div>
                )}
            </div>
        </div>
    )
}

export default WorkloadDashboard
